/**
 * React hook for debt management
 * Provides a clean, React-friendly interface to the debts manager
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { debtsManager } from '../lib/debtsManager';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useDebtMigration } from './useDebtMigration';
import { debtAnalytics } from '../lib/posthog';
import { debtSyncClient } from '../lib/debtSync';

/**
 * Main hook for debt management
 */
export const useDebts = () => {
  const { user } = useAuth();
  const [data, setData] = useState(debtsManager.data);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Always call custom hooks unconditionally
  useDebtMigration();

  // Subscribe to manager updates
  useEffect(() => {
    const unsubscribe = debtsManager.subscribe((newData) => {
      setData(newData);
    });
    setData(debtsManager.data);
    return unsubscribe;
  }, []);

  // Handle login/logout hydration & migration
  useEffect(() => {
    let mounted = true;

    const handleAuthChange = async () => {
      if (user?.id) {
        try {
          if (mounted) setLoading(true);

          // snapshot local debts prior to switching storage key
          const localDebts = debtsManager.data.debts || [];

          debtsManager.setUserStorageKey(user.id);
          debtsManager.reloadData();

          const { debts: hydratedDebts, error: syncError } =
            await debtSyncClient.hydrateFromServerOrMigrate(localDebts);

          if (hydratedDebts) {
            debtsManager.setDebts(hydratedDebts);
            debtSyncClient.mirrorToLocalStorage(hydratedDebts, user.id);

            debtSyncClient.onSyncNeeded(() => {
              const currentDebts = debtsManager.data.debts || [];
              debtSyncClient.debouncedPutAllDebts(currentDebts);
            });
          }

          if (syncError && mounted) {
            setError(`Sync warning: ${syncError}`);
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('[useDebts] Auth change error:', err);
          if (mounted) setError(err.message);
          // fallback to local data under user key
          debtsManager.setUserStorageKey(user.id);
          debtsManager.reloadData();
        } finally {
          if (mounted) {
            setLoading(false);
            setData(debtsManager.data);
          }
        }
      } else {
        // anonymous mode
        debtsManager.setUserStorageKey(null);
        debtsManager.reloadData();
        if (mounted) setData(debtsManager.data);
      }
    };

    handleAuthChange();
    return () => {
      mounted = false;
    };
  }, [user?.id]);

  // Helper to wrap async mutations with loading/error
  const handleAsync = useCallback(async (operation) => {
    try {
      setLoading(true);
      setError(null);
      return await operation();
    } catch (err) {
      setError(err.message || 'An error occurred');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Sync helper after mutations (only when authenticated)
  const syncAfterMutation = useCallback(() => {
    if (user?.id && debtSyncClient.isAuthenticated()) {
      const currentDebts = debtsManager.data.debts || [];
      debtSyncClient.debouncedPutAllDebts(currentDebts);
      debtSyncClient.mirrorToLocalStorage(currentDebts, user.id);
    }
  }, [user?.id]);

  // Centralized accessors
  const debts = useMemo(() => debtsManager.getDebts(), [data]);
  const metrics = useMemo(() => debtsManager.getMetrics(), [data]);

  // ---- Debt operations ----
  const saveDebt = useCallback((debt) => {
    return handleAsync(async () => {
      const result = await debtsManager.saveDebt(debt);
      syncAfterMutation();
      return result;
    });
  }, [handleAsync, syncAfterMutation]);

  const addDebt = useCallback((debt) => {
    const newDebt = {
      ...debt,
      id: debt.id || `debt_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      originalAmount: debt.balance,
    };

    // analytics
    const currentDebts = debtsManager.getDebts({ includeDemo: false });
    const isFirstDebt = currentDebts.length === 0;

    if (isFirstDebt) {
      debtAnalytics.trackFirstDebt({
        debt_type: debt.type,
        amount: debt.balance ?? debt.amount,
        interest_rate: debt.rate ?? debt.interestRate,
        min_payment: debt.minPayment,
      });
    } else {
      debtAnalytics.trackDebtAdded({
        debt_type: debt.type,
        amount: debt.balance ?? debt.amount,
        interest_rate: debt.rate ?? debt.interestRate,
        totalCount: currentDebts.length + 1,
      });
    }

    return handleAsync(async () => {
      const result = await debtsManager.saveDebt(newDebt);
      syncAfterMutation();
      return result;
    });
  }, [handleAsync, syncAfterMutation]);

  const updateDebt = useCallback((debtId, updatedData) => {
    return handleAsync(async () => {
      const list = debtsManager.getDebts({ includeDemo: false });
      const existingDebt = list.find((d) => d.id === debtId);
      if (!existingDebt) throw new Error(`Debt with ID ${debtId} not found`);

      const updatedDebt = {
        ...existingDebt,
        ...updatedData,
        id: debtId,
        updatedAt: new Date().toISOString(),
      };

      if (
        Object.prototype.hasOwnProperty.call(updatedData, 'balance') &&
        updatedData.balance !== existingDebt.balance
      ) {
        const oldBalance = existingDebt.balance || 0;
        const newBalance = updatedData.balance || 0;

        debtAnalytics.trackBalanceUpdate(oldBalance, newBalance, debtId);

        if (newBalance === 0 && oldBalance > 0) {
          debtAnalytics.trackMilestone('debt_eliminated', {
            debt_name: existingDebt.name,
            amount_paid_off: oldBalance,
            debt_type: existingDebt.type,
          });
        }

        const updatedDebts = debtsManager
          .getDebts({ includeDemo: false })
          .map((d) => (d.id === debtId ? { ...d, balance: newBalance } : d));
        checkProgressMilestones(updatedDebts);
      }

      const result = await debtsManager.saveDebt(updatedDebt);
      syncAfterMutation();
      return result;
    });
  }, [handleAsync, syncAfterMutation]);

  const deleteDebt = useCallback((debtId) => {
    return handleAsync(async () => {
      const result = await debtsManager.deleteDebt(debtId);
      syncAfterMutation();
      return result;
    });
  }, [handleAsync, syncAfterMutation]);

  const setDebts = useCallback((nextDebts) => {
    return handleAsync(async () => {
      const result = await debtsManager.setDebts(nextDebts);
      syncAfterMutation();
      return result;
    });
  }, [handleAsync, syncAfterMutation]);

  const reorderDebts = useCallback((orderUpdates) => {
    return handleAsync(async () => {
      const result = await debtsManager.reorderDebts(orderUpdates);
      syncAfterMutation();
      return result;
    });
  }, [handleAsync, syncAfterMutation]);

  const getDebtsInOrder = useCallback(() => {
    // kept for backward-compat
    // eslint-disable-next-line no-console
    console.warn('[useDebts] getDebtsInOrder() is deprecated. Use getDebts() directly.');
    return debtsManager.getDebts();
  }, []);

  // ---- Payments ----
  const paymentHistory = data.paymentHistory || [];

  const recordPayment = useCallback((payment) => {
    return handleAsync(async () => {
      const result = await debtsManager.recordPayment(payment);
      syncAfterMutation();
      return result;
    });
  }, [handleAsync, syncAfterMutation]);

  const getPaymentHistory = useCallback((month = null) => {
    return debtsManager.getPaymentHistory(month);
  }, []);

  const getPayment = useCallback((debtId, month) => {
    return debtsManager.getPayment(debtId, month);
  }, []);

  // ---- Projections ----
  const projections = data.projections;

  const calculateProjections = useCallback(() => {
    return handleAsync(() => debtsManager.calculateProjections());
  }, [handleAsync]);

  const recalculateProjections = useCallback(() => {
    return handleAsync(() => debtsManager.recalculateProjections());
  }, [handleAsync]);

  // ---- Settings ----
  const settings = data.settings || {};
  const extraPayment = settings.extraPayment || 0;

  const updateSettings = useCallback((newSettings) => {
    return handleAsync(() => debtsManager.updateSettings(newSettings));
  }, [handleAsync]);

  const setExtraPayment = useCallback((amount) => {
    return updateSettings({ extraPayment: amount });
  }, [updateSettings]);

  // ---- Utilities ----
  const clearAllData = useCallback(() => {
    return handleAsync(() => debtsManager.clearAllData());
  }, [handleAsync]);

  const exportData = useCallback(() => debtsManager.exportData(), []);
  const importData = useCallback((importedData) => {
    return handleAsync(() => debtsManager.importData(importedData));
  }, [handleAsync]);

  const loadDemoData = useCallback(() => {
    return handleAsync(() => debtsManager.loadDemoData());
  }, [handleAsync]);

  const getCurrentMonth = useCallback(() => debtsManager.getMonthKey(0), []);
  const getMonthKey = useCallback((offset = 0) => debtsManager.getMonthKey(offset), []);

  return {
    // Data
    data,
    debts,
    paymentHistory,
    projections,
    settings,

    // Metrics / computed
    ...metrics,
    extraPayment,

    // State
    loading,
    error,

    // Debt ops
    saveDebt,
    addDebt,
    updateDebt,
    deleteDebt,
    setDebts,
    reorderDebts,
    getDebtsInOrder,

    // Payment ops
    recordPayment,
    getPaymentHistory,
    getPayment,

    // Projections
    calculateProjections,
    recalculateProjections,

    // Settings
    updateSettings,
    setExtraPayment,

    // Utilities
    clearAllData,
    exportData,
    importData,
    loadDemoData,
    getCurrentMonth,
    getMonthKey,
  };
};

/**
 * Hook for managing a specific debt
 */
export const useDebt = (debtId) => {
  const { debts, getPaymentHistory, recordPayment } = useDebts();

  const debt = useMemo(() => debts.find((d) => d.id === debtId), [debts, debtId]);
  const payments = useMemo(
    () => getPaymentHistory().filter((p) => p.debtId === debtId),
    [getPaymentHistory, debtId]
  );

  const recordDebtPayment = useCallback(
    (payment) => recordPayment({ ...payment, debtId }),
    [recordPayment, debtId]
  );

  return {
    debt,
    payments,
    recordPayment: recordDebtPayment,
  };
};

/**
 * Hook for monthly payment tracking
 */
export const useMonthlyPayments = (month) => {
  const { debts, getPaymentHistory, recordPayment, projections, getMonthKey } = useDebts();

  const monthKey = useMemo(() => month || getMonthKey(0), [month, getMonthKey]);
  const monthlyPayments = useMemo(() => getPaymentHistory(monthKey), [getPaymentHistory, monthKey]);
  const projectedData = useMemo(
    () => projections?.months?.find((m) => m.monthKey === monthKey),
    [projections, monthKey]
  );

  const recordMonthlyPayment = useCallback(
    (debtId, payment) =>
      recordPayment({
        ...payment,
        debtId,
        month: monthKey,
      }),
    [recordPayment, monthKey]
  );

  const getPaymentForDebt = useCallback(
    (debtId) => monthlyPayments.find((p) => p.debtId === debtId),
    [monthlyPayments]
  );

  return {
    monthKey,
    monthlyPayments,
    projectedData,
    recordMonthlyPayment,
    getPaymentForDebt,
    debts,
  };
};

// -------- Helpers (non-hook) --------

const checkProgressMilestones = (debts) => {
  const totalOriginal = debts.reduce(
    (sum, debt) => sum + (debt.originalAmount || debt.balance || 0),
    0
  );
  const totalCurrent = debts.reduce((sum, debt) => sum + (debt.balance || 0), 0);
  const totalPaidOff = totalOriginal - totalCurrent;
  const percentPaidOff = totalOriginal > 0 ? (totalPaidOff / totalOriginal) * 100 : 0;

  const milestones = [
    { threshold: 25, type: '25_percent_paid' },
    { threshold: 50, type: '50_percent_paid' },
    { threshold: 75, type: '75_percent_paid' },
    { threshold: 100, type: 'debt_free' },
  ];

  const achieved = JSON.parse(localStorage.getItem('achieved_milestones') || '[]');

  milestones.forEach((m) => {
    if (percentPaidOff >= m.threshold && !achieved.includes(m.type)) {
      debtAnalytics.trackMilestone(m.type, {
        total_paid_off: totalPaidOff,
        percent_complete: Math.round(percentPaidOff),
        total_original_debt: totalOriginal,
        remaining_debt: totalCurrent,
        debts_remaining: debts.filter((d) => (d.balance || 0) > 0).length,
      });

      achieved.push(m.type);
      localStorage.setItem('achieved_milestones', JSON.stringify(achieved));
    }
  });

  const amountMilestones = [1000, 5000, 10000, 25000, 50000];
  const achievedAmounts = JSON.parse(localStorage.getItem('achieved_amount_milestones') || '[]');

  amountMilestones.forEach((amount) => {
    if (totalPaidOff >= amount && !achievedAmounts.includes(amount)) {
      debtAnalytics.trackMilestone('amount_milestone', {
        milestone_amount: amount,
        total_paid_off: totalPaidOff,
        time_to_milestone: calculateDaysFromStart(),
      });

      achievedAmounts.push(amount);
      localStorage.setItem('achieved_amount_milestones', JSON.stringify(achievedAmounts));
    }
  });
};

const calculateDaysFromStart = () => {
  const startDate = localStorage.getItem('debt_journey_start');
  if (!startDate) {
    const now = new Date().toISOString();
    localStorage.setItem('debt_journey_start', now);
    return 0;
  }
  const start = new Date(startDate);
  const now = new Date();
  return Math.floor((now - start) / (1000 * 60 * 60 * 24));
};

export default useDebts;